import { getFirestore, collection, query, orderBy, limit, getDocs, doc, getDoc, updateDoc, addDoc, serverTimestamp, startAfter } from 'firebase/firestore';
import { app } from '../firebase';

const db = getFirestore(app);

export const adminOrderService = {
  /**
   * Fetch orders with filtering and pagination
   * @param {Object} params - Filter parameters
   * @param {string} params.status - Order status filter
   * @param {number} params.limit - Number of orders to fetch
   * @param {Object} params.startAfter - Document to start after for pagination
   * @returns {Promise<Object>} Orders data with pagination info
   */
  async fetchOrders(params = {}) {
    try {
      const { status, limit: limitCount = 20, startAfter: startAfterDoc } = params;
      
      // Simple query without complex filters to avoid indexes
      let q = collection(db, 'deliveryOrders');
      
      // Apply pagination
      if (startAfterDoc) {
        q = query(q, startAfter(startAfterDoc));
      }
      
      q = query(q, orderBy('createdAt', 'desc'), limit(limitCount));
      
      const snapshot = await getDocs(q);
      
      const orders = [];
      snapshot.forEach(doc => {
        const orderData = doc.data();
        
        // Apply filters in memory instead of in query
        if (status && status !== 'all' && orderData.status !== status) {
          return; // Skip this order
        }
        
        orders.push({
          id: doc.id,
          ...orderData,
          createdAt: orderData.createdAt?.toDate?.() || orderData.createdAt
        });
      });
      
      console.log(orders);
      return {
        orders,
        hasMore: orders.length === limitCount,
        lastDoc: snapshot.docs[snapshot.docs.length - 1]
      };
      
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw new Error(error.message || 'Failed to fetch orders');
    }
  },

  /**
   * Get detailed information about a specific order
   * @param {string} orderId - The order ID
   * @returns {Promise<Object>} Order details with customer and reservation info
   */
  async fetchOrderDetails(orderId) {
    try {
      const orderDoc = await getDoc(doc(db, 'deliveryOrders', orderId));
      
      if (!orderDoc.exists()) {
        throw new Error('Order not found');
      }

      const orderData = orderDoc.data();
      
      // Get customer details if available
      let customerData = null;
      if (orderData.userId) {
        try {
          const customerDoc = await getDoc(doc(db, 'users', orderData.userId));
          if (customerDoc.exists()) {
            customerData = customerDoc.data();
          }
        } catch (error) {
          console.warn('Could not fetch customer data:', error);
        }
      }

      // Get reservation details if available
      let reservationData = null;
      if (orderData.reservationId) {
        try {
          const reservationDoc = await getDoc(doc(db, 'reservations', orderData.reservationId));
          if (reservationDoc.exists()) {
            reservationData = reservationDoc.data();
          }
        } catch (error) {
          console.warn('Could not fetch reservation data:', error);
        }
      }
      console.log(orderData);
      return {
        order: {
          id: orderDoc.id,
          ...orderData,
          createdAt: orderData.createdAt?.toDate?.() || orderData.createdAt,
          updatedAt: orderData.updatedAt?.toDate?.() || orderData.updatedAt
        },
        customer: customerData,
        reservation: reservationData
      };
      
    } catch (error) {
      console.error('Error fetching order details:', error);
      throw new Error(error.message || 'Failed to fetch order details');
    }
  },

  /**
   * Update the status of an order
   * @param {string} orderId - The order ID
   * @param {string} status - New status
   * @param {string} notes - Optional notes
   * @returns {Promise<Object>} Updated order data
   */
  async updateOrderStatus(orderId, status, notes = null) {
    try {
      const validStatuses = ['pending', 'confirmed', 'preparing', 'ready', 'served', 'cancelled', 'delivered'];
      if (!validStatuses.includes(status)) {
        throw new Error('Invalid status');
      }

      const updateData = {
        status,
        updatedAt: serverTimestamp()
      };

      if (notes) {
        updateData.notes = notes;
      }

      // Add status history
      const orderRef = doc(db, 'deliveryOrders', orderId);
      const orderDoc = await getDoc(orderRef);
      
      if (!orderDoc.exists()) {
        throw new Error('Order not found');
      }

      const currentData = orderDoc.data();
      const statusHistory = currentData.statusHistory || [];
      
      statusHistory.push({
        status,
        timestamp: new Date(), // Use regular Date instead of serverTimestamp()
        notes: notes || null
      });

      updateData.statusHistory = statusHistory;

      await updateDoc(orderRef, updateData);

      // Get updated order data
      const updatedDoc = await getDoc(orderRef);
      const updatedData = updatedDoc.data();

      return {
        success: true,
        order: {
          id: updatedDoc.id,
          ...updatedData,
          createdAt: updatedData.createdAt?.toDate?.() || updatedData.createdAt,
          updatedAt: updatedData.updatedAt?.toDate?.() || updatedData.updatedAt
        }
      };
      
    } catch (error) {
      console.error('Error updating order status:', error);
      throw new Error(error.message || 'Failed to update order status');
    }
  },

  /**
   * Update comprehensive order details
   * @param {string} orderId - The order ID
   * @param {Object} updateData - Order update data
   * @returns {Promise<Object>} Updated order data
   */
  async updateOrder(orderId, updateData) {
    try {
      const orderRef = doc(db, 'deliveryOrders', orderId);
      const orderDoc = await getDoc(orderRef);
      
      if (!orderDoc.exists()) {
        throw new Error('Order not found');
      }

      const currentData = orderDoc.data();
      
      // Prepare the update data
      const finalUpdateData = {
        ...updateData,
        updatedAt: serverTimestamp()
      };

      // Add status history if status is being updated
      if (updateData.status && updateData.status !== currentData.status) {
        const statusHistory = currentData.statusHistory || [];
        statusHistory.push({
          status: updateData.status,
          timestamp: new Date(),
          notes: updateData.adminNotes || 'Updated by admin'
        });
        finalUpdateData.statusHistory = statusHistory;
      }

      // Update the document
      await updateDoc(orderRef, finalUpdateData);

      // Get updated order data
      const updatedDoc = await getDoc(orderRef);
      const updatedData = updatedDoc.data();

      return {
        success: true,
        order: {
          id: updatedDoc.id,
          ...updatedData,
          createdAt: updatedData.createdAt?.toDate?.() || updatedData.createdAt,
          updatedAt: updatedData.updatedAt?.toDate?.() || updatedData.updatedAt
        }
      };
      
    } catch (error) {
      console.error('Error updating order:', error);
      throw new Error(error.message || 'Failed to update order');
    }
  },

  /**
   * Get order statistics for a specific period
   * @param {string} period - Time period ('today', 'week', 'month')
   * @returns {Promise<Object>} Order statistics
   */
  async fetchOrderStats(period = 'today') {
    try {
      let startDate;
      const now = new Date();
      
      switch (period) {
        case 'today':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case 'week':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
          break;
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
          break;
        default:
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      }

      // Simple query without complex filters to avoid indexes
      const q = query(
        collection(db, 'deliveryOrders'),
        orderBy('createdAt', 'desc'),
        limit(1000) // Get more results to calculate stats
      );
      
      const snapshot = await getDocs(q);

      const stats = {
        totalOrders: 0,
        totalRevenue: 0,
        statusCounts: {
          pending: 0,
          confirmed: 0,
          preparing: 0,
          ready: 0,
          served: 0,
          cancelled: 0,
          delivered: 0
        },

      };

      snapshot.forEach(doc => {
        const orderData = doc.data();
        
        // Filter by date in memory
        const orderDate = orderData.createdAt?.toDate?.() || orderData.createdAt;
        if (orderDate && orderDate < startDate) {
          return; // Skip orders before start date
        }
        
        // Skip deleted orders
        if (orderData.deleted) {
          return;
        }
        
        stats.totalOrders++;
        stats.totalRevenue += orderData.totalAmount || 0;
        
        if (orderData.status) {
          stats.statusCounts[orderData.status] = (stats.statusCounts[orderData.status] || 0) + 1;
        }
        

      });

      return stats;
      
    } catch (error) {
      console.error('Error fetching order stats:', error);
      throw new Error(error.message || 'Failed to fetch order statistics');
    }
  },

  /**
   * Search orders by various criteria
   * @param {string} query - Search query
   * @param {number} limit - Maximum number of results
   * @returns {Promise<Object>} Search results
   */
  async searchOrders(query, limitCount = 20) {
    try {
      if (!query || query.trim().length < 2) {
        throw new Error('Search query must be at least 2 characters');
      }

      const searchQuery = query.trim().toLowerCase();
      
      // Simple query without complex filters to avoid indexes
      const q = query(
        collection(db, 'deliveryOrders'),
        orderBy('createdAt', 'desc'),
        limit(100) // Get more results to search through
      );

      const snapshot = await getDocs(q);
      
      // Search in memory instead of using complex queries
      const allOrders = [];
      snapshot.forEach(doc => {
        const orderData = doc.data();
        
        // Check if order matches search criteria
        const matchesSearch = 
          (orderData.orderId && orderData.orderId.toLowerCase().includes(searchQuery)) ||
          (orderData.customerName && orderData.customerName.toLowerCase().includes(searchQuery)) ||
          (orderData.userEmail && orderData.userEmail.toLowerCase().includes(searchQuery)) ||
          (orderData.customerPhone && orderData.customerPhone.includes(searchQuery));
        
        if (matchesSearch) {
          allOrders.push({
            id: doc.id,
            ...orderData,
            createdAt: orderData.createdAt?.toDate?.() || orderData.createdAt
          });
        }
      });

      // Sort by creation date and limit results
      const orders = allOrders
        .sort((a, b) => b.createdAt - a.createdAt)
        .slice(0, limitCount);

      return { orders };
      
    } catch (error) {
      console.error('Error searching orders:', error);
      throw new Error(error.message || 'Failed to search orders');
    }
  },

  /**
   * Soft delete an order
   * @param {string} orderId - The order ID
   * @param {string} reason - Reason for deletion
   * @returns {Promise<Object>} Success status
   */
  async deleteOrder(orderId, reason = null) {
    try {
      await updateDoc(doc(db, 'deliveryOrders', orderId), {
        deleted: true,
        deletedAt: serverTimestamp(),
        deleteReason: reason || null
      });

      return { success: true };
      
    } catch (error) {
      console.error('Error deleting order:', error);
      throw new Error(error.message || 'Failed to delete order');
    }
  },

  /**
   * Export order data
   * @param {string} startDate - Start date for export
   * @param {string} endDate - End date for export
   * @param {string} format - Export format ('json' or 'csv')
   * @returns {Promise<Object>} Exported data
   */
  async exportOrders(startDate = null, endDate = null, format = 'json') {
    try {
      // Simple query without complex filters to avoid indexes
      const q = query(
        collection(db, 'deliveryOrders'),
        orderBy('createdAt', 'desc'),
        limit(1000) // Get more results for export
      );
      
      const snapshot = await getDocs(q);
      
      const orders = [];
      snapshot.forEach(doc => {
        const orderData = doc.data();
        
        // Filter by date in memory
        if (startDate && endDate) {
          const orderDate = orderData.createdAt?.toDate?.() || orderData.createdAt;
          const startTimestamp = new Date(startDate);
          const endTimestamp = new Date(endDate);
          
          if (orderDate && (orderDate < startTimestamp || orderDate > endTimestamp)) {
            return; // Skip orders outside date range
          }
        }
        
        // Skip deleted orders
        if (orderData.deleted) {
          return;
        }
        
        orders.push({
          id: doc.id,
          ...orderData,
          createdAt: orderData.createdAt?.toDate?.() || orderData.createdAt,
          updatedAt: orderData.updatedAt?.toDate?.() || orderData.updatedAt
        });
      });

      if (format === 'csv') {
        // Convert to CSV format
        const csvHeaders = ['Order ID', 'Customer Name', 'Phone', 'Status', 'Total Amount', 'Created At', 'Items'];
        const csvRows = orders.map(order => [
          order.orderId || order.id,
          order.customerName || order.userEmail || '',
          order.customerPhone || '',
          order.status || '',
          order.totalAmount || 0,
          order.createdAt ? order.createdAt.toISOString() : '',
          order.dishes ? order.dishes.map(item => `${item.dishName} (${item.count})`).join('; ') : ''
        ]);
        
        const csvContent = [csvHeaders, ...csvRows]
          .map(row => row.map(field => `"${field}"`).join(','))
          .join('\n');
        
        return { format: 'csv', data: csvContent };
      }

      return { format: 'json', data: orders };
      
    } catch (error) {
      console.error('Error exporting orders:', error);
      throw new Error(error.message || 'Failed to export orders');
    }
  },

  /**
   * Create a new order
   * @param {Object} orderData - Order data
   * @returns {Promise<Object>} Created order
   */
  async createOrder(orderData) {
    try {
      const newOrder = {
        orderId: this.generateOrderId(),
        userId: orderData.userId,
        userEmail: orderData.userEmail,
        customerName: orderData.customerName,
        customerPhone: orderData.customerPhone,
        customerEmail: orderData.customerEmail,

        status: "pending",
        dishes: orderData.dishes || orderData.items || [],
        deliveryAddress: orderData.deliveryAddress || null,
        specialRequest: orderData.specialRequest || orderData.notes || "none",
        paymentMethod: orderData.paymentMethod || null,
        subtotal: orderData.subtotal || this.calculateSubtotal(orderData.dishes || orderData.items || []),
        serviceFee: orderData.serviceFee || 0,
        totalAmount: orderData.totalAmount || (orderData.subtotal + (orderData.serviceFee || 0)),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        statusHistory: [
          {
            status: "pending",
            timestamp: new Date(), // Use regular Date instead of serverTimestamp()
            notes: "Order created"
          }
        ],
        deleted: false,
        deletedAt: null,
        deletedBy: null,
        deleteReason: null
      };

      const docRef = await addDoc(collection(db, 'deliveryOrders'), newOrder);
      
      return {
        id: docRef.id,
        ...newOrder
      };
      
    } catch (error) {
      console.error('Error creating order:', error);
      throw new Error(error.message || 'Failed to create order');
    }
  },

  /**
   * Get status badge configuration
   * @returns {Object} Status badge styles
   */
  getStatusBadgeConfig() {
    return {
      pending: {
        bgColor: 'bg-yellow-500',
        textColor: 'text-white',
        dotColor: 'bg-yellow-600',
        label: 'Pending'
      },
      confirmed: {
        bgColor: 'bg-blue-500',
        textColor: 'text-white',
        dotColor: 'bg-blue-600',
        label: 'Confirmed'
      },
      preparing: {
        bgColor: 'bg-orange-500',
        textColor: 'text-white',
        dotColor: 'bg-orange-600',
        label: 'Preparing'
      },
      ready: {
        bgColor: 'bg-purple-500',
        textColor: 'text-white',
        dotColor: 'bg-purple-600',
        label: 'Ready'
      },
      served: {
        bgColor: 'bg-green-500',
        textColor: 'text-white',
        dotColor: 'bg-green-600',
        label: 'Served'
      },
      cancelled: {
        bgColor: 'bg-red-500',
        textColor: 'text-white',
        dotColor: 'bg-red-600',
        label: 'Cancelled'
      },
      delivered: {
        bgColor: 'bg-green-500',
        textColor: 'text-white',
        dotColor: 'bg-green-600',
        label: 'Delivered'
      }
    };
  },

  /**
   * Format order data for display
   * @param {Object} order - Raw order data
   * @returns {Object} Formatted order data
   */
  formatOrderForDisplay(order) {
    const statusConfig = this.getStatusBadgeConfig();
    const status = statusConfig[order.status] || statusConfig.pending;
    
    return {
      ...order,
      // Use customerName or userEmail as fallback
      customerName: order.customerName || order.userEmail || 'N/A',
      // Use dishes array for items
      items: order.dishes || order.items || [],
      // Format time and date
      formattedTime: order.createdAt ? new Date(order.createdAt).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      }) : '',
      formattedDate: order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }) : '',
      formattedAmount: order.totalAmount ? `${order.totalAmount.toFixed(2)}` : '0.00',
      statusConfig,
      displayStatus: status
    };
  },

  /**
   * Calculate order summary statistics
   * @param {Array} orders - Array of orders
   * @returns {Object} Summary statistics
   */
  calculateOrderSummary(orders) {
    const summary = {
      totalOrders: orders.length,
      totalRevenue: 0,
      statusCounts: {}
    };

    orders.forEach(order => {
      summary.totalRevenue += order.totalAmount || 0;
      
      // Count by status
      summary.statusCounts[order.status] = (summary.statusCounts[order.status] || 0) + 1;
    });

    return summary;
  },

  /**
   * Generate order ID
   * @returns {string} Generated order ID
   */
  generateOrderId() {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `ORD-${year}-${month}${day}-${random}`;
  },

  /**
   * Calculate subtotal
   * @param {Array} items - Order items
   * @returns {number} Subtotal
   */
  calculateSubtotal(items) {
    return items.reduce((total, item) => total + (item.price * item.quantity), 0);
  },

  /**
   * Calculate tax (10% example)
   * @param {Array} items - Order items
   * @returns {number} Tax amount
   */
  calculateTax(items) {
    const subtotal = this.calculateSubtotal(items);
    return subtotal * 0.1; // 10% tax
  },

  /**
   * Calculate total
   * @param {Array} items - Order items
   * @returns {number} Total amount
   */
  calculateTotal(items) {
    const subtotal = this.calculateSubtotal(items);
    const tax = this.calculateTax(items);
    return subtotal + tax;
  }
};

export default adminOrderService; 